import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

export default function Home() {
  const [scrollY, setScrollY] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    document.title = 'Kristensen Byg og Montage ApS'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Kristensen Byg og Montage ApS udfører kvalitetsbyggeri, renoveringer, montage og specialopgaver. Mere end 15 års erfaring med fokus på solide løsninger og godt håndværk.'
      )
    }
  }, [])

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error'

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes'
    } else if (!/^[0-9\s+()-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email skal udfyldes'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig emailadresse'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse skal udfyldes'
    }
    return newErrors
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        })
      } else {
        setSubmitStatus('error')
      }
    } catch (err) {
      console.error('Submit error:', err)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Helper to get Cloudflare resized image URL
  const getResizedUrl = (url, width, quality = 80) => {
    if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url
    return url.replace(
      'https://assets.auxodev.dk/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`
    )
  }

  // Gallery State & Logic
  const [galleries, setGalleries] = useState([])
  const [galleryLoading, setGalleryLoading] = useState(true)
  const [galleryError, setGalleryError] = useState(null)
  const [activeTab, setActiveTab] = useState('alle') // 'alle', 'renovering', 'nybyg', 'porebeton'
  const [activeImageIndex, setActiveImageIndex] = useState(null)

  useEffect(() => {
    let isMounted = true
    async function fetchGalleries() {
      try {
        setGalleryLoading(true)
        const res = await fetch('https://api.dev.auxo.dk/public/content/gallery')
        if (res.ok) {
          const data = await res.json()
          if (isMounted) {
            setGalleries(data)
            setGalleryLoading(false)
          }
        } else {
          throw new Error('Kunne ikke hente gallerier')
        }
      } catch (err) {
        console.error('Error fetching galleries:', err)
        if (isMounted) {
          setGalleryError('Kunne ikke indlæse galleribilleder.')
          setGalleryLoading(false)
        }
      }
    }
    fetchGalleries()
    return () => {
      isMounted = false
    }
  }, [])

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (activeImageIndex === null) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveImageIndex(null)
      } else if (e.key === 'ArrowRight') {
        handleNextImage()
      } else if (e.key === 'ArrowLeft') {
        handlePrevImage()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeImageIndex, galleries, activeTab])

  // Prevent background scroll when lightbox is open
  useEffect(() => {
    if (activeImageIndex !== null) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [activeImageIndex])

  // Get current filtered images
  const getFilteredImages = () => {
    if (activeTab === 'alle') {
      return galleries.flatMap((g) =>
        (g.images || []).map((img) => ({
          url: img,
          title: g.title,
          slug: g.slug?._ || g.slug,
        }))
      )
    }
    const found = galleries.find(
      (g) => g.slug?._ === activeTab || g.slug === activeTab
    )
    if (!found || !found.images) return []
    return found.images.map((img) => ({
      url: img,
      title: found.title,
      slug: found.slug?._ || found.slug,
    }))
  }

  const filteredImages = getFilteredImages().slice(0, 6)

  const handlePrevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? filteredImages.length - 1 : prev - 1
    )
  }

  const handleNextImage = () => {
    setActiveImageIndex((prev) =>
      prev === filteredImages.length - 1 ? 0 : prev + 1
    )
  }

  const heroImage = 'https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/hero-craftsman-danish-construction.jpg'
  const whyChooseUsImage = 'https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/why-choose-us-construction.jpg'

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-anthracite text-white py-16 md:py-24 lg:py-32 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={getResizedUrl(heroImage, 1600, 85)}
            alt="Moderne dansk byggeplads med fokus på kvalitetsbyggeri"
            className="h-full w-full object-cover opacity-70 transition-transform duration-75 ease-out"
            style={{ transform: `scale(${1 + scrollY * 0.0004})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-anthracite/60 via-anthracite/45 to-anthracite/50 lg:bg-gradient-to-r lg:from-anthracite/75 lg:via-anthracite/45 lg:to-anthracite/20" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Text Content */}
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold uppercase tracking-wider leading-tight text-white mb-6">
                Kvalitet i byggeriet – også når opgaven kræver lidt ekstra
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-warm-gray-200 leading-relaxed mb-10 max-w-2xl">
                Specialist i professionel opsætning af porebeton samt kvalitetsløsninger inden for nybyg og renovering. Med over 15 års erfaring er fokus altid på præcisionsarbejde, godt håndværk og solide løsninger.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="rounded bg-brand-green px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200"
                >
                  Kontakt mig
                </Link>
                <Link
                  to="/porebeton"
                  className="rounded border border-white/40 bg-white/10 backdrop-blur-xs px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-white hover:text-anthracite transition-colors duration-200"
                >
                  Læs om Porebeton
                </Link>
              </div>
            </div>

            {/* Right Column: Webform */}
            <div className="lg:col-span-5 animate-shake-on-load">
              <div className="bg-white/95 backdrop-blur-md border border-warm-gray-200 p-6 sm:p-8 rounded-lg shadow-2xl">
                <h3 className="text-xl font-bold uppercase tracking-wider text-anthracite mb-2">Få et uforpligtende tilbud</h3>
                <p className="text-xs sm:text-sm text-warm-gray-600 mb-6">
                  Udfyld formularen, så vender jeg tilbage til dig hurtigst muligt.
                </p>

                {submitStatus === 'success' ? (
                  <div className="bg-brand-green/10 border border-brand-green/30 p-6 rounded text-center">
                    <div className="h-12 w-12 rounded-full bg-brand-green/20 text-brand-green flex items-center justify-center mx-auto mb-4">
                      <i className="fa-solid fa-check text-xl" />
                    </div>
                    <h4 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-2">Tak for din henvendelse!</h4>
                    <p className="text-sm text-warm-gray-700">
                      Jeg har modtaget din besked og vender tilbage til dig hurtigst muligt.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Honeypot */}
                    <div className="sr-only absolute -top-9999 -left-9999">
                      <label htmlFor="website_url">Website URL</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label htmlFor="hero-name" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="hero-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full rounded bg-warm-gray-50 border ${
                          errors.name ? 'border-red-500' : 'border-warm-gray-200'
                        } px-4 py-2.5 text-sm text-anthracite placeholder-warm-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors`}
                        placeholder="Dit fulde navn"
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="hero-phone" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                          Telefon <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="hero-phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full rounded bg-warm-gray-50 border ${
                            errors.phone ? 'border-red-500' : 'border-warm-gray-200'
                          } px-4 py-2.5 text-sm text-anthracite placeholder-warm-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors`}
                          placeholder="Telefonnummer"
                        />
                        {errors.phone && (
                          <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="hero-email" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="hero-email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full rounded bg-warm-gray-50 border ${
                            errors.email ? 'border-red-500' : 'border-warm-gray-200'
                          } px-4 py-2.5 text-sm text-anthracite placeholder-warm-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors`}
                          placeholder="din@email.dk"
                        />
                        {errors.email && (
                          <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="hero-description" className="block text-xs font-bold uppercase tracking-wider text-warm-gray-700 mb-1.5">
                        Beskrivelse af opgaven <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="hero-description"
                        name="description"
                        rows="3"
                        value={formData.description}
                        onChange={handleChange}
                        className={`w-full rounded bg-warm-gray-50 border ${
                          errors.description ? 'border-red-500' : 'border-warm-gray-200'
                        } px-4 py-2.5 text-sm text-anthracite placeholder-warm-gray-400 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors resize-none`}
                        placeholder="Beskriv dit projekt..."
                      />
                      {errors.description && (
                        <p className="mt-1 text-xs text-red-500">{errors.description}</p>
                      )}
                    </div>

                    {submitStatus === 'error' && (
                      <div className="bg-red-900/20 border border-red-500/30 p-3 rounded text-center text-xs text-red-400">
                        Der skete en fejl under afsendelsen. Prøv venligst igen eller ring direkte til mig.
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full rounded bg-brand-green py-3 px-4 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="fa-solid fa-spinner animate-spin" />
                          Sender...
                        </>
                      ) : (
                        <>
                          send forespørgsel
                          <i className="fa-light fa-paper-plane" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strengths Trust Banner */}
      <section className="bg-anthracite-light border-y border-warm-gray-800 py-10 md:py-12 relative z-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Strength 1 */}
            <div className="flex items-center gap-4">
              <div className="text-brand-green text-3xl shrink-0">
                <i className="fa-light fa-award" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">Erfaring</h3>
                <p className="text-xs text-warm-gray-400 mt-1">Udlært i 2011 med bred erfaring</p>
              </div>
            </div>

            {/* Strength 2 */}
            <div className="flex items-center gap-4">
              <div className="text-brand-green text-3xl shrink-0">
                <i className="fa-light fa-screwdriver-wrench" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">Kvalitet</h3>
                <p className="text-xs text-warm-gray-400 mt-1">Håndværk udført efter højeste standarder</p>
              </div>
            </div>

            {/* Strength 3 */}
            <div className="flex items-center gap-4">
              <div className="text-brand-green text-3xl shrink-0">
                <i className="fa-light fa-compass-drafting" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">Komplekse opgaver</h3>
                <p className="text-xs text-warm-gray-400 mt-1">Specialløsninger der kræver præcision</p>
              </div>
            </div>

            {/* Strength 4 */}
            <div className="flex items-center gap-4">
              <div className="text-brand-green text-3xl shrink-0">
                <i className="fa-light fa-handshake" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">Ordentlighed</h3>
                <p className="text-xs text-warm-gray-400 mt-1">Aftaler overholdes og ryddet op bagefter</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ydelsesoversigt Section */}
      <section id="ydelser" className="bg-white py-16 md:py-24 border-b border-warm-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite">
              Professionelle løsninger til dit byggeprojekt
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {/* Porebeton Card - Prioritized First */}
            <div className="bg-warm-gray-50 border-2 border-brand-green/30 overflow-hidden shadow-sm flex flex-col sm:flex-row group hover:shadow-md transition-shadow duration-300 relative rounded-lg">
              <div className="sm:w-2/5 relative min-h-[220px] sm:min-h-full overflow-hidden">
                <img
                  src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20260701-165032.jpg", 500, 80)}
                  alt="Professionelt arbejde med porebeton og gasbeton"
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-102 transition-transform duration-500"
                />
              </div>
              <div className="p-6 sm:p-8 sm:w-3/5 flex flex-col justify-between">
                <div>
                  <div className="inline-block bg-brand-green/10 text-brand-green text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded mb-2">
                    Specialiseret montørarbejde
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-wider text-anthracite mb-2">Porebeton & Gasbeton</h3>
                  <p className="text-xs sm:text-sm text-warm-gray-600 leading-relaxed mb-4">
                    Professionel opbygning af skillevægge, indervægge og bagmure med millimeterpræcision og høj isoleringsevne.
                  </p>
                </div>
                <div>
                  <Link
                    to="/porebeton"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-brand-green hover:text-brand-green-hover transition-colors cursor-pointer"
                  >
                    Læs mere om porebeton
                    <i className="fa-light fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Nybyg Card */}
            <div className="bg-warm-gray-50 border border-warm-gray-200 overflow-hidden shadow-sm flex flex-col sm:flex-row group hover:shadow-md transition-shadow duration-300 rounded-lg">
              <div className="sm:w-2/5 relative min-h-[200px] sm:min-h-full overflow-hidden">
                <img
                  src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/newbuild-funkis-house.jpg", 400, 80)}
                  alt="Moderne nybygget funkishus"
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-102 transition-transform duration-500"
                />
              </div>
              <div className="p-6 sm:p-8 sm:w-3/5 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-2">Nybyg</h3>
                  <p className="text-xs sm:text-sm text-warm-gray-600 leading-relaxed mb-4">
                    Stor erfaring fra nybyg og tæt samarbejde med typehusfirmaer og entreprenører.
                  </p>
                </div>
                <div>
                  <Link
                    to="/nybyg"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-black hover:text-neutral-700 transition-colors cursor-pointer"
                  >
                    læs mere om nybyg
                    <i className="fa-light fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Renovering Card */}
            <div className="bg-warm-gray-50 border border-warm-gray-200 overflow-hidden shadow-sm flex flex-col sm:flex-row group hover:shadow-md transition-shadow duration-300 rounded-lg">
              <div className="sm:w-2/5 relative min-h-[200px] sm:min-h-full overflow-hidden">
                <img
                  src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/renovation-modern-kitchen.jpg", 400, 80)}
                  alt="Moderne renoveret køkken"
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-102 transition-transform duration-500"
                />
              </div>
              <div className="p-6 sm:p-8 sm:w-3/5 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-2">Renovering</h3>
                  <p className="text-xs sm:text-sm text-warm-gray-600 leading-relaxed mb-4">
                    Renoveringsopgaver med fokus på kvalitet, funktionalitet og holdbare løsninger.
                  </p>
                </div>
                <div>
                  <Link
                    to="/renovering"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-black hover:text-neutral-700 transition-colors cursor-pointer"
                  >
                    læs mere om renovering
                    <i className="fa-light fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Specialopgaver Card */}
            <div className="bg-warm-gray-50 border border-warm-gray-200 overflow-hidden shadow-sm flex flex-col sm:flex-row group hover:shadow-md transition-shadow duration-300 rounded-lg">
              <div className="sm:w-2/5 relative min-h-[200px] sm:min-h-full overflow-hidden">
                <img
                  src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/special-solutions-carpentry.jpg", 400, 80)}
                  alt="Skræddersyede specialløsninger"
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-102 transition-transform duration-500"
                />
              </div>
              <div className="p-6 sm:p-8 sm:w-3/5 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold uppercase tracking-wider text-anthracite mb-2">Specialopgaver</h3>
                  <p className="text-xs sm:text-sm text-warm-gray-600 leading-relaxed mb-4">
                    Skræddersyede løsninger og specialopgaver, hvor der skal tænkes kreativt og udføres præcisionsarbejde.
                  </p>
                </div>
                <div>
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-black hover:text-neutral-700 transition-colors cursor-pointer"
                  >
                    hør om dit projekt
                    <i className="fa-light fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dedicated Porebeton Showcase Section */}
      <section className="bg-anthracite text-white py-16 md:py-24 border-b border-warm-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Content */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 rounded bg-brand-green/20 px-3 py-1 text-xs font-bold text-brand-green-light uppercase tracking-wider border border-brand-green/30">
                <i className="fa-light fa-cubes text-sm" />
                <span>Specialiseret håndværk</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold uppercase tracking-wider text-white leading-tight">
                Porebeton & Gasbeton til moderne byggeri
              </h2>
              <p className="text-base sm:text-lg text-warm-gray-300 leading-relaxed font-light">
                Opsætning af indervægge, bagmure og skillevægge i porebeton udføres med millimeterpræcision og fokus på snorlige linjer.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="flex items-start gap-3 bg-anthracite-light p-4 rounded border border-warm-gray-800">
                  <i className="fa-solid fa-check text-brand-green mt-1 shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Skillevægge & Indervægge</h4>
                    <p className="text-xs text-warm-gray-400 mt-1">Snorlige opbygning klar til malerbehandling.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-anthracite-light p-4 rounded border border-warm-gray-800">
                  <i className="fa-solid fa-check text-brand-green mt-1 shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Bagmure i Råhus</h4>
                    <p className="text-xs text-warm-gray-400 mt-1">Stærke, isolerende bagmure til nybyg og tilbyg.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-anthracite-light p-4 rounded border border-warm-gray-800">
                  <i className="fa-solid fa-check text-brand-green mt-1 shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Høj isoleringsværdi</h4>
                    <p className="text-xs text-warm-gray-400 mt-1">Optimal lyddæmpning og temperaturstyring.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-anthracite-light p-4 rounded border border-warm-gray-800">
                  <i className="fa-solid fa-check text-brand-green mt-1 shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Erfaren montør</h4>
                    <p className="text-xs text-warm-gray-400 mt-1">Mange års praktisk erfaring med porebetonelementer.</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/porebeton"
                  className="rounded bg-brand-green px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200 uppercase tracking-wider inline-flex items-center gap-2"
                >
                  Udforsk porebeton løsninger
                  <i className="fa-light fa-arrow-right text-xs" />
                </Link>
                <Link
                  to="/kontakt"
                  className="rounded border border-warm-gray-700 bg-anthracite-light px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-warm-gray-800 transition-colors duration-200 uppercase tracking-wider"
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </div>

            {/* Right Column: Visual Collage of Real Project Photos */}
            <div className="lg:col-span-6 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="overflow-hidden rounded-lg border border-warm-gray-800 shadow-xl aspect-4/3">
                  <img
                    src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20260701-165032.jpg", 600, 80)}
                    alt="Porebeton skillevægge under opbygning"
                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="overflow-hidden rounded-lg border border-warm-gray-800 shadow-xl aspect-square">
                  <img
                    src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20260618-123603.jpg", 600, 80)}
                    alt="Præcisionsarbejde med porebeton"
                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-6 sm:pt-10">
                <div className="overflow-hidden rounded-lg border border-warm-gray-800 shadow-xl aspect-square">
                  <img
                    src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20260529-164139.jpg", 600, 80)}
                    alt="Opbygning af råhus i porebeton"
                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="overflow-hidden rounded-lg border border-warm-gray-800 shadow-xl aspect-4/3">
                  <img
                    src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/20201005-073811.jpg", 600, 80)}
                    alt="Porebeton projekt under udførelse"
                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro & Strengths Section */}
      <section className="relative bg-warm-gray-50 py-16 md:py-24 border-b border-warm-gray-200 overflow-hidden">
        {/* Slanted architectural background shape on the left side */}
        <div className="hidden xl:block absolute inset-y-0 left-0 w-1/4 bg-brand-green -skew-x-12 origin-top-left pointer-events-none transform -translate-x-1/3" />
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Brief Introduction with Image on Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Content */}
            <div className="lg:col-span-7 text-left lg:order-first">
              <p className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite mb-6">
                Erfaren tømrermester med fokus på ordentlighed
              </p>
              <p className="text-sm sm:text-base text-warm-gray-600 leading-relaxed mb-6">
                Kristensen Byg og Montage ApS er stiftet med et klart mål om at levere tømrer- og montagearbejde af højeste kvalitet. Jeg blev udlært i 2011 og har siden opbygget stor erfaring og faglig stolthed:
              </p>
              <ul className="space-y-3 mb-8 text-sm sm:text-base text-warm-gray-600">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-brand-green mt-1 flex-shrink-0" />
                  <span><strong>Højeste kvalitet</strong> – Solidt tømrerarbejde med stor faglig stolthed.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-brand-green mt-1 flex-shrink-0" />
                  <span><strong>Bred erfaring</strong> – Udlært i 2011 med solid erfaring inden for faget.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check text-brand-green mt-1 flex-shrink-0" />
                  <span><strong>Ordentlighed & dialog</strong> – Gennemsigtige aftaler og løsninger, der holder.</span>
                </li>
              </ul>
              <div>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center rounded bg-brand-green px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200 uppercase tracking-wider cursor-pointer"
                >
                  Kontakt mig
                  <i className="fa-light fa-arrow-right ml-2 text-xs" />
                </Link>
              </div>
            </div>

            {/* Right Column: Image */}
            <div className="lg:col-span-5 relative aspect-4/3 lg:aspect-square overflow-hidden border border-warm-gray-200 shadow-md lg:order-last">
              <img
                src={getResizedUrl("https://assets.auxodev.dk/assets/8ba53ab1-a105-4ab3-8373-4041e8d1e44e/about-planning-craftsman.jpg", 600, 80)}
                alt="Tømrermester planlægger byggeprojekt"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Hvorfor vælge os Section */}
      <section className="bg-white py-16 md:py-24 border-b border-warm-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left/Right Column: Content first on mobile, last on desktop */}
            <div className="flex flex-col justify-center lg:order-last">
              <h3 className="text-2xl sm:text-3xl font-bold uppercase tracking-wider text-anthracite mb-6">
                Når erfaring og kvalitet skal gå hånd i hånd
              </h3>
              <div className="space-y-6 text-sm sm:text-base text-warm-gray-600 leading-relaxed">
                <p>
                  Hos Kristensen Byg og Montage ApS bygger jeg på et solidt fundament af faglig stolthed og mere end 15 års erfaring. Siden 2011 har jeg leveret solidt tømrerarbejde til både private og erhverv.
                </p>
                <p>
                  Jeg lægger stor vægt på ordentlighed, gennemsigtig dialog og uovertruffen kvalitet. Når du vælger mig, får du en samarbejdspartner, der holder ord og leverer løsninger, der holder.
                </p>
              </div>
              <div className="mt-10">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center rounded bg-brand-green px-6 py-3.5 text-sm font-bold text-white shadow-sm hover:bg-brand-green-hover transition-colors duration-200"
                >
                  Få en uforpligtende dialog
                </Link>
              </div>
            </div>

            {/* Image Column: last on mobile, first on desktop */}
            <div className="relative aspect-4/3 overflow-hidden border border-warm-gray-200 shadow-md lg:order-first">
              <img
                src={getResizedUrl(whyChooseUsImage, 800, 80)}
                alt="Moderne dansk byggeprojekt under blå himmel"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Galleri Section */}
      <section className="bg-warm-gray-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold uppercase tracking-wider text-anthracite mb-4">
              Mine Projekter
            </h2>
            <p className="text-sm sm:text-base text-warm-gray-600 leading-relaxed">
              Se et udvalg af mit udførte arbejde inden for renovering, nybyg og porebeton.
            </p>
          </div>

          {/* Tab Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {[
              { id: 'alle', label: 'ALLE PROJEKTER' },
              { id: 'porebeton', label: 'POREBETON' },
              { id: 'renovering', label: 'RENOVERING' },
              { id: 'nybyg', label: 'NYBYG' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id)
                  setActiveImageIndex(null)
                }}
                className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider rounded transition-all duration-200 cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-brand-green text-white shadow-sm'
                    : 'bg-white text-warm-gray-600 border border-warm-gray-200 hover:bg-warm-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          {galleryLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-warm-gray-200 border-t-brand-green" />
              <p className="mt-4 text-sm text-warm-gray-500 font-medium">Henter galleri...</p>
            </div>
          ) : galleryError || filteredImages.length === 0 ? (
            <div className="rounded-lg bg-white p-8 text-center border border-warm-gray-200 shadow-sm max-w-md mx-auto">
              <i className="fa-light fa-images text-3xl text-warm-gray-400 mb-3 block" />
              <p className="text-sm text-warm-gray-600 font-medium">
                {galleryError || 'Ingen billeder tilgængelige i dette galleri.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImageIndex(idx)}
                  className="group relative aspect-4/3 overflow-hidden bg-white border border-warm-gray-200 shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2 cursor-pointer"
                >
                  <img
                    src={getResizedUrl(img.url, 600)}
                    alt={`${img.title} referencebillede ${idx + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-anthracite/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full bg-white/90 flex items-center justify-center text-brand-green shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300">
                      <i className="fa-light fa-magnifying-glass-plus text-lg" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Fullscreen Lightbox */}
        {activeImageIndex !== null && filteredImages[activeImageIndex] && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm animate-fade-in duration-200">
            {/* Close Button */}
            <button
              onClick={() => setActiveImageIndex(null)}
              className="absolute top-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus:outline-none cursor-pointer"
              aria-label="Luk galleri"
            >
              <i className="fa-light fa-xmark text-xl" />
            </button>

            {/* Navigation Buttons */}
            <button
              onClick={handlePrevImage}
              className="absolute left-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus:outline-none cursor-pointer"
              aria-label="Forrige billede"
            >
              <i className="fa-light fa-chevron-left text-xl" />
            </button>

            <button
              onClick={handleNextImage}
              className="absolute right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus:outline-none cursor-pointer"
              aria-label="Næste billede"
            >
              <i className="fa-light fa-chevron-right text-xl" />
            </button>

            {/* Image Container */}
            <div className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center justify-center">
              <img
                src={getResizedUrl(filteredImages[activeImageIndex].url, 1600, 85)}
                alt={`${filteredImages[activeImageIndex].title} fuld skærm ${activeImageIndex + 1}`}
                className="max-h-[80vh] max-w-full object-contain rounded-md shadow-2xl select-none"
              />
              {/* Image Caption & Counter */}
              <div className="mt-4 text-center">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-green-light block mb-1">
                  {filteredImages[activeImageIndex].title}
                </span>
                <div className="text-sm font-medium text-warm-gray-400">
                  {activeImageIndex + 1} / {filteredImages.length}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
